/**
 * Model + persona-voice resolution for native embeds. Extracted from
 * NativeLiveAvatar.tsx (7 Aug 2026 voice-mismatch round) so the voice-pin
 * regression test can import it without dragging the component's CSS
 * imports into node:test.
 *
 * Every branch that can serve the platform-default Arc model must resolve
 * a `voiceId`: NativeLiveAvatar pins `options.voiceId || selectedModel
 * .voiceId` on every imperative speak() so no TTS request ever falls
 * through to the engine's service default (en-AU-NatashaNeural — a female
 * voice on the male Arc model).
 */
import { getModelById, getModelByUrl, getDefaultModel, resolveModelUrl, type ModelConfig } from '../core/modelRegistry';

export interface ResolveSelectedModelOptions {
  modelUrl?: string;
  requestedAvatarModelId?: string;
  requestedLiveVoiceId?: string;
  useEmbeddedTextures?: boolean;
}

export function resolveSelectedModel({
  modelUrl,
  requestedAvatarModelId,
  requestedLiveVoiceId,
  useEmbeddedTextures
}: ResolveSelectedModelOptions): ModelConfig {
  const registryModel = requestedAvatarModelId
    ? getModelById(requestedAvatarModelId)
    : undefined;
  if (registryModel) {
    return registryModel;
  }

  if (modelUrl) {
    // If the URL points at a known registry model (relative, resolved-CDN, or
    // lowres form), inherit that model's config — critically its textureMode.
    // Consumers that pass a bare modelUrl (hosted sites, external hosts) were
    // otherwise defaulted to textureMode 'external', which painted the legacy
    // Sam/Xandra webp texture set over embedded-texture models like Arc
    // (split-tone face, black brow/lash decals — Keith's "textures are wrong"
    // report, Jul 23 2026). The consumer's exact URL still wins so an
    // app-side HD/SD choice is preserved.
    const registryByUrl = getModelByUrl(modelUrl);
    if (registryByUrl) {
      return {
        ...registryByUrl,
        url: resolveModelUrl(modelUrl),
        // Pin the consumer's exact URL: dropping lowresUrl makes the
        // downstream mobile-aware quality resolution a no-op, so a host that
        // deliberately chose HD (or SD) keeps that choice on every device.
        lowresUrl: undefined,
        voiceId: requestedLiveVoiceId || registryByUrl.voiceId
      };
    }
    return {
      id: requestedAvatarModelId || 'embedded-live-avatar',
      name: requestedAvatarModelId || 'Embedded Live Avatar',
      url: resolveModelUrl(modelUrl),
      textureMode: useEmbeddedTextures ? 'embedded' : 'external',
      blendshapeFormat: 'arkit',
      voiceId: requestedLiveVoiceId
    };
  }

  const defaultModel = getDefaultModel();
  return defaultModel ?? {
    id: 'default',
    name: 'Arc',
    url: resolveModelUrl('/models/arc.glb'),
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    voiceId: requestedLiveVoiceId || 'en-AU-WilliamNeural'
  };
}
